// ==================== ADMIN SETTINGS PAGE ====================

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Settings,
  User,
  Lock,
  Bell,
  Globe,
  Mail,
  Shield,
  Save,
  Loader2,
  CheckCircle,
  Building2,
  Palette,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Toggle } from '../../components/ui/Toggle';
import { fadeInUp, staggerContainer } from '../../utils/animations';
import { useAuth } from '../../contexts';
import { MESSAGES } from '../../constants/messages.constants';
import { authService, platformSettingsService } from '../../services';

// ==================== VALIDATION SCHEMAS ====================

const profileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[@$!%*?&]/, 'Password must contain at least one special character (@$!%*?&)'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: MESSAGES.ERROR.PASSWORD_MISMATCH,
  path: ['confirmPassword'],
});

const platformSchema = z.object({
  platformName: z.string().min(1, 'Platform name is required'),
  supportEmail: z.string().email(MESSAGES.VALIDATION.EMAIL_INVALID),
  defaultCurrency: z.string().min(1, 'Currency is required'),
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;
type PlatformFormData = z.infer<typeof platformSchema>;

// ==================== SETTINGS TAB COMPONENT ====================

interface SettingsTabProps {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}

const SettingsTab = ({ label, icon, isActive, onClick }: SettingsTabProps) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left transition-all duration-200 ${
      isActive
        ? 'bg-(--color-orange-glow) text-(--color-signal-orange) border border-[rgba(230,142,55,0.3)]'
        : 'text-(--color-soft-gray) hover:bg-white/5 hover:text-white'
    }`}
  >
    {icon}
    <span className="font-medium">{label}</span>
  </button>
);

// ==================== MAIN COMPONENT ====================

const AdminSettingsPage = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Profile Form
  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors, isSubmitting: isSubmittingProfile, isValid: isProfileValid },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    },
  });

  // Password Form
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors, isSubmitting: isSubmittingPassword, isValid: isPasswordValid },
    reset: resetPassword,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    mode: 'onChange',
  });

  // Platform Form
  const {
    register: registerPlatform,
    handleSubmit: handleSubmitPlatform,
    formState: { errors: platformErrors, isSubmitting: isSubmittingPlatform, isValid: isPlatformValid },
    reset: resetPlatform,
  } = useForm<PlatformFormData>({
    resolver: zodResolver(platformSchema),
    mode: 'onChange',
  });

  // Platform settings loading state
  const [platformLoading, setPlatformLoading] = useState(true);

  // Fetch platform settings on mount
  useEffect(() => {
    const fetchPlatformSettings = async () => {
      try {
        const response = await platformSettingsService.getSettings();
        if (response.success && response.data) {
          resetPlatform({
            platformName: response.data.platformName || 'AmplifyMsg',
            supportEmail: response.data.supportEmail || '',
            defaultCurrency: response.data.defaultCurrency || 'USD',
          });
        }
      } catch (error) {
        console.error('Failed to fetch platform settings:', error);
      } finally {
        setPlatformLoading(false);
      }
    };

    fetchPlatformSettings();
  }, [resetPlatform]);

  // Notification Settings State
  const [notifications, setNotifications] = useState({
    emailNewCustomer: true,
    emailSubscription: true,
    emailPayment: true,
    browserNotifications: false,
  });

  // Handle Profile Submit
  const onProfileSubmit = async (data: ProfileFormData) => {
    setSaveError(null);
    try {
      const response = await authService.updateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
      });

      if (response.success && response.data) {
        // Update user in auth context
        updateUser(response.data);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      setSaveError('An unexpected error occurred. Please try again.');
    }
  };

  // Handle Password Submit
  const onPasswordSubmit = async (data: PasswordFormData) => {
    setSaveError(null);
    try {
      const response = await authService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      if (response.success) {
        resetPassword();
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(response.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setSaveError('An unexpected error occurred. Please try again.');
    }
  };

  // Handle Platform Submit
  const onPlatformSubmit = async (data: PlatformFormData) => {
    setSaveError(null);
    try {
      const response = await platformSettingsService.updateSettings({
        platformName: data.platformName,
        supportEmail: data.supportEmail,
        defaultCurrency: data.defaultCurrency,
      });

      if (response.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(response.message || 'Failed to update platform settings');
      }
    } catch (error) {
      console.error('Failed to update platform settings:', error);
      setSaveError('An unexpected error occurred. Please try again.');
    }
  };

  // Settings Tabs
  const tabs = [
    { id: 'profile', label: 'Profile', icon: <User size={20} /> },
    { id: 'security', label: 'Security', icon: <Lock size={20} /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={20} /> },
    { id: 'platform', label: 'Platform', icon: <Building2 size={20} /> },
  ];

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div variants={fadeInUp}>
        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white flex items-center gap-3">
          <Settings className="text-(--color-signal-orange)" />
          Settings
        </h1>
        <p className="text-(--color-muted-gray) mt-1">
          Manage your account and platform settings
        </p>
      </motion.div>

      {/* Success Message */}
      <AnimatePresence>
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-xl bg-(--color-success)/10 border border-(--color-success)/30"
          >
            <CheckCircle size={20} className="text-(--color-success)" />
            <p className="text-sm text-(--color-success)">Settings saved successfully!</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      <AnimatePresence>
        {saveError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-xl bg-(--color-error)/10 border border-(--color-error)/30"
          >
            <AlertCircle size={20} className="text-(--color-error)" />
            <p className="text-sm text-(--color-error) flex-1">{saveError}</p>
            <Button variant="ghost" size="sm" onClick={() => setSaveError(null)}>
              Dismiss
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar Tabs */}
        <motion.div variants={fadeInUp} className="lg:col-span-1">
          <Card className="p-2">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <SettingsTab
                  key={tab.id}
                  label={tab.label}
                  icon={tab.icon}
                  isActive={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                />
              ))}
            </nav>
          </Card>
        </motion.div>

        {/* Content Area */}
        <motion.div variants={fadeInUp} className="lg:col-span-3">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-(--color-signal-orange)/10">
                  <User size={24} className="text-(--color-signal-orange)" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Profile Information</h2>
                  <p className="text-sm text-(--color-muted-gray)">Update your account details</p>
                </div>
              </div>

              <form onSubmit={handleSubmitProfile(onProfileSubmit)} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    placeholder="Enter first name"
                    {...registerProfile('firstName')}
                    error={profileErrors.firstName?.message}
                  />
                  <Input
                    label="Last Name"
                    placeholder="Enter last name"
                    {...registerProfile('lastName')}
                    error={profileErrors.lastName?.message}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-(--color-muted-gray)" />
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full pl-10 pr-4 py-3 bg-(--color-navy-light)/50 border border-(--color-border-gray) rounded-md text-(--color-muted-gray) cursor-not-allowed"
                    />
                  </div>
                  <p className="text-xs text-(--color-muted-gray) mt-1">
                    Email cannot be changed. Contact support if you need to update it.
                  </p>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSubmittingProfile}
                    disabled={!isProfileValid || isSubmittingProfile}
                    leftIcon={isSubmittingProfile ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-(--color-signal-orange)/10">
                  <Shield size={24} className="text-(--color-signal-orange)" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Security Settings</h2>
                  <p className="text-sm text-(--color-muted-gray)">Manage your password and security</p>
                </div>
              </div>

              <form onSubmit={handleSubmitPassword(onPasswordSubmit)} className="space-y-6">
                <Input
                  label="Current Password"
                  type="password"
                  placeholder="Enter current password"
                  leftIcon={<Lock size={18} />}
                  showPasswordToggle
                  {...registerPassword('currentPassword')}
                  error={passwordErrors.currentPassword?.message}
                />
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="New Password"
                    type="password"
                    placeholder="Enter new password"
                    showPasswordToggle
                    {...registerPassword('newPassword')}
                    error={passwordErrors.newPassword?.message}
                  />
                  <Input
                    label="Confirm New Password"
                    type="password"
                    placeholder="Confirm new password"
                    showPasswordToggle
                    {...registerPassword('confirmPassword')}
                    error={passwordErrors.confirmPassword?.message}
                  />
                </div>
                <div className="p-4 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                  <h3 className="text-sm font-medium text-white mb-2">Password Requirements</h3>
                  <ul className="text-sm text-(--color-muted-gray) space-y-1">
                    <li>• At least 8 characters long</li>
                    <li>• Contains at least one uppercase letter</li>
                    <li>• Contains at least one lowercase letter</li>
                    <li>• Contains at least one number</li>
                    <li>• Contains at least one special character (@$!%*?&)</li>
                  </ul>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSubmittingPassword}
                    disabled={!isPasswordValid || isSubmittingPassword}
                    leftIcon={isSubmittingPassword ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  >
                    Update Password
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-(--color-signal-orange)/10">
                  <Bell size={24} className="text-(--color-signal-orange)" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Notification Preferences</h2>
                  <p className="text-sm text-(--color-muted-gray)">Choose how you want to be notified</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-white flex items-center gap-2">
                    <Mail size={16} className="text-(--color-signal-orange)" />
                    Email Notifications
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                      <div>
                        <p className="text-sm font-medium text-white">New Customer Registration</p>
                        <p className="text-xs text-(--color-muted-gray)">Get notified when a new customer registers</p>
                      </div>
                      <Toggle
                        checked={notifications.emailNewCustomer}
                        onChange={() => setNotifications(prev => ({ ...prev, emailNewCustomer: !prev.emailNewCustomer }))}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                      <div>
                        <p className="text-sm font-medium text-white">Subscription Changes</p>
                        <p className="text-xs text-(--color-muted-gray)">Get notified about subscription updates</p>
                      </div>
                      <Toggle
                        checked={notifications.emailSubscription}
                        onChange={() => setNotifications(prev => ({ ...prev, emailSubscription: !prev.emailSubscription }))}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                      <div>
                        <p className="text-sm font-medium text-white">Payment Notifications</p>
                        <p className="text-xs text-(--color-muted-gray)">Get notified about payments and invoices</p>
                      </div>
                      <Toggle
                        checked={notifications.emailPayment}
                        onChange={() => setNotifications(prev => ({ ...prev, emailPayment: !prev.emailPayment }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-white flex items-center gap-2">
                    <Globe size={16} className="text-(--color-signal-orange)" />
                    Browser Notifications
                  </h3>
                  
                  <div className="flex items-center justify-between p-4 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                    <div>
                      <p className="text-sm font-medium text-white">Push Notifications</p>
                      <p className="text-xs text-(--color-muted-gray)">Receive browser push notifications</p>
                    </div>
                    <Toggle
                      checked={notifications.browserNotifications}
                      onChange={() => setNotifications(prev => ({ ...prev, browserNotifications: !prev.browserNotifications }))}
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    leftIcon={<Save size={16} />}
                    onClick={() => {
                      setSaveSuccess(true);
                      setTimeout(() => setSaveSuccess(false), 3000);
                    }}
                  >
                    Save Preferences
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Platform Tab */}
          {activeTab === 'platform' && (
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-(--color-signal-orange)/10">
                  <Palette size={24} className="text-(--color-signal-orange)" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Platform Settings</h2>
                  <p className="text-sm text-(--color-muted-gray)">Configure global platform settings</p>
                </div>
              </div>

              {platformLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-(--color-signal-orange)" />
                </div>
              ) : (
                <form onSubmit={handleSubmitPlatform(onPlatformSubmit)} className="space-y-6">
                  <Input
                    label="Platform Name"
                    placeholder="Enter platform name"
                    leftIcon={<Building2 size={18} />}
                    {...registerPlatform('platformName')}
                    error={platformErrors.platformName?.message}
                  />
                  <Input
                    label="Support Email"
                    type="email"
                    placeholder="Enter support email"
                    leftIcon={<Mail size={18} />}
                    {...registerPlatform('supportEmail')}
                    error={platformErrors.supportEmail?.message}
                  />
                  <Input
                    label="Default Currency"
                    placeholder="e.g., USD, EUR, GBP"
                    leftIcon={<Globe size={18} />}
                    {...registerPlatform('defaultCurrency')}
                    error={platformErrors.defaultCurrency?.message}
                  />
                  
                  <div className="p-4 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                    <h3 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                      <Shield size={16} className="text-(--color-signal-orange)" />
                      API Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-(--color-muted-gray)">API Version:</span>
                        <span className="text-white font-mono">v1</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-(--color-muted-gray)">Environment:</span>
                        <span className="text-(--color-success) font-mono">{import.meta.env.MODE === 'production' ? 'Production' : 'Development'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-(--color-muted-gray)">API Base URL:</span>
                        <span className="text-white font-mono text-xs">{import.meta.env.VITE_API_BASE_URL + '/' + import.meta.env.VITE_API_PREFIX + '/' + import.meta.env.VITE_API_VERSION}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={isSubmittingPlatform}
                      disabled={!isPlatformValid || isSubmittingPlatform}
                      leftIcon={isSubmittingPlatform ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    >
                      Save Platform Settings
                    </Button>
                  </div>
                </form>
              )}
            </Card>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export { AdminSettingsPage };

